import { useParams } from 'react-router-dom';
import { PageContainer } from '../../../components/PageContainer';
import styled, { useTheme } from 'styled-components';
import { DownloadIcon } from '../../../assets/icons/download';
import { formatNumberToCurrency } from '../../../utils';
import { useGetInvoicesQuery } from '../api/invoicesApi';
import { FullScreenSpinner } from '../../../components/Spinner/Spinner';

// https://dribbble.com/shots/22678392-Accounts-invoices

export const Invoices = () => {
  const { stripeId } = useParams<{ stripeId: string }>();
  const { colors } = useTheme();

  const { data: invoices, isLoading, isError, refetch } = useGetInvoicesQuery({
    customerId: stripeId || ''
  });

  if (isLoading) {
    return (
      <PageContainer title="Invoices" onGoBack="/dashboard">
        <FullScreenSpinner />
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer title="Invoices" onGoBack="/dashboard">
        <EmptyStateWrapper>
          <EmptyStateTitle>Couldn't load your invoices</EmptyStateTitle>
          <EmptyStateText>
            Something went wrong on our side. Please try again, or contact support if it keeps
            happening.
          </EmptyStateText>
          <RetryButton type="button" onClick={() => refetch()}>
            Try again
          </RetryButton>
        </EmptyStateWrapper>
      </PageContainer>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <PageContainer title="Invoices" onGoBack="/dashboard">
        <EmptyStateWrapper>
          <EmptyStateTitle>No invoices yet</EmptyStateTitle>
          <EmptyStateText>
            Your invoices will appear here after your first payment.
          </EmptyStateText>
        </EmptyStateWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Invoices" onGoBack="/dashboard">
      <TableWrapper>
        <Table>
          <TableRow>
            <TableHeader>Amount</TableHeader>
            <TableHeader>Plan</TableHeader>
            <TableHeader>Created</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell>
                <AmountWrapper>
                  <AmountText>
                    {formatNumberToCurrency(invoice.amount_due / 100, invoice.currency)}
                  </AmountText>
                  <InvoiceId>#{invoice.id.slice(-8)}</InvoiceId>
                </AmountWrapper>
              </TableCell>
              <TableCell>
                <PlanText>{invoice.lines[0]?.description ?? '—'}</PlanText>
              </TableCell>
              <TableCell>
                <DateText>{invoice.created_date}</DateText>
              </TableCell>
              <TableCell>
                <StatusBadge status={invoice.status}>{invoice.status}</StatusBadge>
              </TableCell>
              <TableCell>
                <ActionCell>
                  {invoice.status === 'open' && invoice.hosted_invoice_url ? (
                    <PayNowBtn href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                      Pay now
                    </PayNowBtn>
                  ) : invoice.invoice_pdf ? (
                    <DownloadBtn href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer">
                      <DownloadIcon color={colors.primary} />
                      Download
                    </DownloadBtn>
                  ) : null}
                </ActionCell>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </TableWrapper>
    </PageContainer>
  );
};

const TABLE_RADIUS = '0.75rem';

const AmountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const AmountText = styled.span`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.black};
`;

const InvoiceId = styled.span`
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: ${({ theme }) => theme.colors.gray900};
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
`;

const PlanText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.black};
  font-weight: 500;
`;

const DateText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
`;

const ActionCell = styled.div`
  display: flex;
  justify-content: center;
`;

type InvoiceStatus = 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';

const statusColor = (status: InvoiceStatus, colors: Record<string, string>): string => {
  switch (status) {
    case 'paid':
      return colors.green;
    case 'open':
      return colors.yellow900;
    case 'uncollectible':
      return colors.red;
    default: // draft, void
      return colors.gray500;
  }
};

const StatusBadge = styled.div<{ status: InvoiceStatus }>`
  padding: 0.375rem 0.75rem;
  background: ${({ status, theme }) => {
    const color = statusColor(status, theme.colors);
    return `linear-gradient(135deg, ${color}, ${color}90)`;
  }};
  text-align: center;
  border-radius: 0.5rem;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  font-weight: 600;
  text-transform: capitalize;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ status, theme }) => statusColor(status, theme.colors)}20;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  text-align: center;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: ${TABLE_RADIUS};
`;

const EmptyStateTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.medium};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
  max-width: 28rem;
`;

const RetryButton = styled.button`
  margin-top: 0.5rem;
  padding: 0.5rem 1.25rem;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const DownloadBtn = styled.a`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  gap: 0.375rem;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  text-decoration: none;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary}10;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.25rem 0.5rem;
    gap: 0.25rem;
    font-size: 0.7rem;
  }
`;

const PayNowBtn = styled(DownloadBtn)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}90);
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  
  &:hover {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}90, ${({ theme }) => theme.colors.primary}80);
    color: ${({ theme }) => theme.colors.white};
  }
`;

const TableWrapper = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.gray}08);
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: ${TABLE_RADIUS};
  overflow: hidden;
  max-width: 100%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow-x: auto;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }
  
  @media (max-width: 768px) {
    border-radius: 0.5rem;
    margin: 0 -1rem;
    border-left: none;
    border-right: none;
  }
`;

const Table = styled.table`
  border-spacing: 0px;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  min-width: 600px;
  
  @media (max-width: 768px) {
    min-width: 500px;
  }
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  
  &:not(:first-child):hover {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.gray}15, ${({ theme }) => theme.colors.gray}08);
  }
  
  &:last-child > td {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem 0.75rem;
  background-color: transparent;
  font-size: ${({ theme }) => theme.fontSize.small};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray500}15;
  vertical-align: middle;
  
  &:first-child {
    padding-left: 1.5rem;
  }
  
  &:last-child {
    padding-right: 1.5rem;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 1rem 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 600;
  border-bottom: 2px solid ${({ theme }) => theme.colors.gray500}20;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.gray}25, ${({ theme }) => theme.colors.gray}15);
  color: ${({ theme }) => theme.colors.black};
  text-transform: uppercase;
  letter-spacing: 0.025em;
  
  &:first-child {
    padding-left: 1.5rem;
    border-top-left-radius: ${TABLE_RADIUS};
  }
  
  &:last-child {
    padding-right: 1.5rem;
    border-top-right-radius: ${TABLE_RADIUS};
  }
`;
